import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { RvQuoteForm } from "@/components/RvQuoteForm";
import hero from "@/assets/rv-retractable-awning.jpg";
import heroPicture from "@/assets/rv-retractable-awning.jpg?w=640;1024;1600;1920&format=avif;webp;jpeg&as=picture";
import upholstery from "@/assets/rv-upholstery-interior.jpg";

import carpet from "@/assets/rv-carpet-binding.jpg";
import rvProtectiveTarp from "@/assets/rv-travel-trailer-cover.png";

export const Route = createFileRoute("/rv-covers")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "RV Awnings, Shade Panels & Upholstery in Texas | Texas Made Shade",
      description: "Retractable RV awnings, pull-out slide-toppers, shade panels, custom carpet & binding, upholstery replacement, BBQ covers.",
      image: hero,
      imageAlt: "Retractable RV awning extended over a Class A motorhome at a Texas campground",
      socialTitle: "RV Awnings, Shade & Interiors — Custom Built in Texas",
      socialDescription: "Retractable awnings, slide-toppers, shade panels, carpet binding, upholstery, BBQ covers and generator covers for RVs, motorhomes and 5th wheels.",
      keywords: "RV retractable awnings Texas, RV slide topper, pull out shade RV, RV carpet binding, RV upholstery replacement, RV shade panels, RV BBQ grill cover, RV generator cover, motorhome awning Seguin TX, 5th wheel awning San Antonio",
    }),
    scripts: serviceHeadScripts({
      path: "/rv-covers",
      serviceName: "RV Covers & Upholstery",
      breadcrumbName: "RV Covers",
      description: "RV awnings, generator covers, upholstery, carpet binding and covers — full RV canvas shop in Seguin, TX.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/rv-covers"
      serviceName="RV Awnings & Interiors"
      eyebrow="★ RV AWNINGS & INTERIORS ★"
      title="Retractable RV Awnings, Shade Panels, Upholstery & Covers"
      intro="Built for the Texas sun. Custom retractable awnings, pull-out slide shades, fresh carpet with bound edges, replacement upholstery, exterior shade panels, and heavy-duty covers for generators and gear — all patterned, sewn and installed in our Seguin, TX shop."
      heroImage={heroPicture}
      heroCta={{
        eyebrow: "Free On-Site Measure",
        headline: "Upgrade Your Rig Before the Next Trip",
        subhead: "Awnings, shades, upholstery, carpet and covers — one shop, one team, built to last.",
        primaryHref: "#rv-quote",
        primaryLabel: "Build Your RV Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      features={[
        "Motorized & manual retractable patio awnings with wind sensors",
        "Pull-out slide-out toppers patterned to your rig",
        "Drop-down shade panels & sun screens for the awning side",
        "Replacement RV carpet with professionally serged binding",
        "Full upholstery replacement — captain's chairs, dinettes, sofas",
        "Custom covers for portable generators, propane tanks & gear",
        "Full-rig protective tarps for RVs & travel trailers",
        "Custom windshield protective covers — Class A, Class C & van fronts",
      ]}
      body={[
        {
          heading: "Retractable RV Awnings",
          text: "Replace that brittle factory awning with a heavy-duty motorized or manual retractable awning. We use solution-dyed acrylic fabrics that won't fade, mildew, or chalk in the Texas sun, with reinforced arms and optional wind sensors that auto-retract.",
          bullets: [
            "Motorized with remote, manual hand-crank, or 12V conversions",
            "Solution-dyed acrylic — 10+ year fade warranty",
            "Wind, sun and rain sensor options",
          ],
          image: { src: hero, alt: "Retractable patio awning extended over a Class A motorhome at a Texas RV park" },
        },
        {
          heading: "Pull-Out Slide-Out Toppers & Shade Panels",
          text: "Slide-out toppers protect your slides from leaves, water and UV every time you retract. We also build drop-down shade panels and sun screens that hang off the awning to block the low afternoon sun and turn your patio into a real outdoor room.",
          bullets: [
            "Custom slide toppers patterned to each slide",
            "Drop-down shade panels with grommets & tie-downs",
            "Mesh sun screens that block 90%+ UV but keep the view",
          ],
        },
        {
          heading: "Carpet Replacement & Binding",
          text: "Worn-out RV carpet drags down the whole interior. We pull, pattern and replace cab carpet, slide carpet and bedroom carpet — and serge-bind every edge so it lays flat and won't fray.",
          bullets: [
            "Cut-to-fit cab, slide and bedroom carpet",
            "Professional serged edge binding (color-matched)",
            "Custom bound rugs and runners for any RV",
          ],
          image: { src: carpet, alt: "Freshly installed gray RV carpet with professionally bound edges inside a motorhome" },
        },
        {
          heading: "Upholstery Replacement",
          text: "Captain's chairs cracked? Dinette cushions flat? We strip and rebuild RV furniture with marine-grade vinyl, automotive leather, or Sunbrella® fabric — new foam, new fabric, factory-look stitching.",
          bullets: [
            "Captain's chairs, sofas, dinettes, jackknife sleepers",
            "Marine vinyl, automotive leather, or Sunbrella®",
            "New high-density foam cores",
          ],
          image: { src: upholstery, alt: "Replacement tan leather captain's chairs and dinette upholstery inside an RV motorhome" },
        },
        {
          heading: "Protective Tarps for RVs & Travel Trailers",
          text: "Texas sun, hail and dust will chalk gel coat, crack seals and fade decals long before your rig is worn out. We build custom-patterned protective tarps for travel trailers, 5th wheels, toy haulers and motorhomes — full-rig covers and partial covers (front cap, roof, rear cap) cut to your exact length, height and slide layout. Marine-grade fabric, breathable to prevent trapped moisture, with reinforced corners, strap channels and tie-down points so wind doesn't chew through the cover.",
          bullets: [
            "Full-coverage and front-cap / rear-cap only options",
            "Heavy marine-grade, breathable fabric — won't trap moisture",
            "Reinforced corners, strap channels and ratchet tie-downs",
            "Patterned to your slides, AC shrouds, ladders and antennas",
          ],
          image: {
            src: rvProtectiveTarp,
            alt: "Custom-patterned protective front-cap tarp on a Hideout travel trailer parked at a Texas campsite — Texas Made Shade",
          },
        },
        {
          heading: "Custom RV Windshield Protective Covers",
          text: "We also build custom windshield protective covers for Class A motorhomes, Class C cab-overs and Sprinter / Transit camper vans. Cut to the exact curve of your front cap, the cover blocks UV that fades the dash, keeps the cab 20–30°F cooler in summer, and protects the windshield from rock chips, hail, bugs and bird damage while parked or in storage.",
          bullets: [
            "Patterned to fit Class A, Class C and Sprinter / Transit fronts",
            "UV-blocking outer + soft inner liner that won't scratch glass",
            "Magnetic, snap or strap attachment — no adhesive on paint",
            "Cutouts for mirrors, wipers and tow-camera as needed",
          ],
        },
      ]}
      applications={[
        "Class A Motorhomes",
        "Class B Camper Vans",
        "Class C Motorhomes",
        "Travel Trailers",
        "5th Wheels",
        "Toy Haulers",
        "Park Models",
        "Vintage Restorations",
      ]}
      faqs={[
        { q: "Do you come to me to measure?", a: "Yes — for most Central and South Texas customers we measure on-site. You can also bring the rig to our Seguin shop." },
        { q: "What awning fabric do you use?", a: "Solution-dyed acrylic for retractable awnings (Sunbrella® and equivalents) — it won't fade, mildew or chalk and carries a 10-year fade warranty. For covers and toppers we use heavy vinyl-coated polyester or Sunbrella® Marine." },
        { q: "Can you match my interior fabric or color?", a: "Yes. We carry a deep library of marine vinyls, automotive leathers and Sunbrella® patterns, and can match your existing interior or take it in a new direction." },
        { q: "How long does an upholstery or carpet job take?", a: "Most full RV interior refreshes (carpet + upholstery) are 1–2 weeks in our shop once materials arrive. Awnings and covers usually 1–2 weeks from measure to install." },
        { q: "Do you install or just fabricate?", a: "Both. We fabricate everything in-house in Seguin and install on-site or in our shop." },
        { q: "Do you make full protective tarps for travel trailers and 5th wheels?", a: "Yes — we build custom-patterned protective tarps for travel trailers, 5th wheels, toy haulers and motorhomes. Full-rig or partial (front cap, roof, rear cap) using breathable marine-grade fabric with reinforced corners and tie-downs. We measure on-site so the cover fits your exact slides, AC shrouds and antennas." },
        { q: "Can you build a custom windshield cover for a Class A or Sprinter van?", a: "Yes. We pattern windshield protective covers for Class A motorhomes, Class C cab-overs and Sprinter / Transit camper vans. UV-blocking outer with a soft inner liner, with magnetic, snap or strap attachment and cutouts for mirrors and tow-cameras." },
        { q: "How do you size an RV or travel trailer cover correctly?", a: "We measure on-site whenever possible — exact length bumper-to-bumper (including spare tires and ladders), max height (including A/C shrouds, antennas and vents), and width including any slide-outs in their travel position. If you can't bring the rig to Seguin we'll send a measurement worksheet with diagrams so the finished cover fits like it was tailored, with no flapping or stretched seams." },
        { q: "What's the difference between marine-grade and standard cover fabric?", a: "Standard polyester covers are lighter and cheaper but typically last 1–3 Texas seasons before UV chalking, mildew and seam failure. Marine-grade fabrics (Sunbrella® Marine, vinyl-coated polyester, Top Gun®) are solution-dyed, breathable, mildew-resistant and carry 5–10 year fade warranties. For full-time outdoor storage in the Texas sun we strongly recommend marine-grade — the cover usually outlasts two or three standard ones." },
        { q: "What attachment options do you offer for RV covers and tarps?", a: "We spec the attachment to match how you use the rig. Options include heavy-duty ratchet straps with under-belly buckles, sewn-in strap channels and D-rings, elastic shock cord hems, grommet-and-rope tie-downs, snap or twist-lock fasteners for partial covers, and magnetic edges for windshield and metal-panel covers. All hardware is stainless or UV-stable so it doesn't rust onto your paint." },
        { q: "Can I leave the cover on for seasonal storage all winter or summer?", a: "Yes — that's exactly what they're built for. Use a breathable marine-grade cover (never a non-breathable poly tarp) so condensation can escape and seals don't rot. Before covering, clean and dry the rig, retract slides, pad sharp corners (ladders, antennas, A/C shrouds), and check straps every few weeks during high winds. With proper prep our covers handle full Texas summer storage or winter layup without issue." },
      ]}
    >
      <RvQuoteForm />
    </ServicePage>
  ),
});
